import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useConsent } from "@/components/consent/ConsentProvider";
import CookieBannerModal from "@/components/consent/CookieBannerModal";

/**
 * Bottom bar, CNIL-compliant. Three actions of equal visual weight
 * (Accepter, Refuser, Personnaliser), no dark pattern, no "X" that means
 * accept-by-default. Kept to a single compact row on desktop so it never
 * covers the page's first screen.
 *
 * Hidden once the user has made an explicit choice for the current version.
 * Bumping `CONSENT_VERSION` in `src/lib/consent.ts` brings the bar back.
 */
export default function CookieBanner() {
  const { t } = useTranslation();
  const { hasDecided, acceptAll, rejectAll, open } = useConsent();

  // The modal is always mounted so the footer "Mes choix cookies" button can
  // re-open it even after the bar is dismissed.
  return (
    <>
      <CookieBannerModal />
      {!hasDecided && (
        <div
          role="dialog"
          aria-label={t("legal.cookies.banner.ariaLabel")}
          aria-modal="false"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-foreground bg-background shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.25)]"
        >
          <div className="mx-auto flex max-w-[1200px] flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:gap-6">
            <p className="min-w-0 flex-1 text-xs leading-5 text-muted-foreground md:text-[13px]">
              <span className="font-medium text-foreground">{t("legal.cookies.banner.title")}.</span>{" "}
              {t("legal.cookies.banner.description")}{" "}
              <Link to="/politique-rgpd" className="text-primary hover:underline">
                {t("legal.cookies.banner.privacyLink")}
              </Link>{" "}
              ·{" "}
              <Link to="/cookies" className="text-primary hover:underline">
                {t("legal.cookies.banner.cookiesLink")}
              </Link>
            </p>
            <div className="grid flex-none grid-cols-3 gap-2 md:flex">
              <Button size="sm" onClick={acceptAll}>
                {t("legal.cookies.banner.accept")}
              </Button>
              <Button size="sm" onClick={rejectAll} variant="secondary">
                {t("legal.cookies.banner.reject")}
              </Button>
              <Button size="sm" onClick={open} variant="outline">
                {t("legal.cookies.banner.customize")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
